import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useQuotes } from "../context/QuotesContext";
import QuotePreview from "../components/QuotePreview";
import { uid } from "../mock";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { ArrowLeft, Download, Plus, Trash2, Check, MessageCircle, Loader2 } from "lucide-react";
import { toast } from "../hooks/use-toast";

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    {label && <Label className="text-xs font-medium text-slate-500">{label}</Label>}
    {children}
  </div>
);

const QuoteBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getQuote, updateQuote, ready } = useQuotes();
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const previewRef = useRef(null);

  useEffect(() => {
    if (ready) {
      const q = getQuote(id);
      if (q) setDraft(JSON.parse(JSON.stringify(q)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, id]);

  useEffect(() => {
    if (draft) updateQuote(draft.id, () => draft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  if (!draft) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 text-slate-400">
        Cargando cotización...
      </div>
    );
  }

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const setItem = (itemId, patch) =>
    setDraft((d) => ({ ...d, items: d.items.map((it) => (it.id === itemId ? { ...it, ...patch } : it)) }));
  const addItem = () =>
    setDraft((d) => ({ ...d, items: [...d.items, { id: uid(), service: "Nuevo servicio", total: "$0 MXN" }] }));
  const removeItem = (itemId) =>
    setDraft((d) => ({ ...d, items: d.items.filter((it) => it.id !== itemId) }));

  const generatePdf = async () => {
    const node = previewRef.current;
    if (!node) return null;
    const canvas = await html2canvas(node, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ orientation: "portrait", unit: "px", format: [canvas.width, canvas.height] });
    pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
    return pdf;
  };

  const handleDownload = async () => {
    try {
      setBusy(true);
      const pdf = await generatePdf();
      if (pdf) {
        pdf.save(`${(draft.name || "cotizacion").replace(/[^a-z0-9]+/gi, "-")}.pdf`);
        toast({ title: "PDF descargado" });
      }
    } catch (e) {
      toast({ title: "Error al generar el PDF", description: String(e).slice(0, 80) });
    } finally {
      setBusy(false);
    }
  };

  const handleWhatsapp = async () => {
    try {
      setBusy(true);
      const pdf = await generatePdf();
      if (pdf) pdf.save(`${(draft.name || "cotizacion").replace(/[^a-z0-9]+/gi, "-")}.pdf`);
    } catch (e) {
      // ignore, still open whatsapp
    } finally {
      setBusy(false);
    }
    const digits = (draft.whatsapp || "").replace(/[^\d]/g, "");
    const msg = encodeURIComponent(
      `Hola, te comparto la cotización "${draft.name}" de ${draft.brandTitle}. Adjunto el PDF que acabo de descargar.`
    );
    window.open(`https://wa.me/${digits}?text=${msg}`, "_blank");
    toast({ title: "Abriendo WhatsApp", description: "Adjunta el PDF descargado en el chat." });
  };

  return (
    <div className="flex h-screen flex-col bg-slate-100">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/cotizaciones")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0">
            <input
              value={draft.name}
              onChange={(e) => set({ name: e.target.value })}
              className="w-full truncate border-none bg-transparent font-display text-base font-bold text-slate-900 outline-none"
            />
            <p className="flex items-center gap-1 text-xs text-emerald-600">
              <Check className="h-3 w-3" /> Guardado automáticamente
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" disabled={busy} onClick={handleDownload}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Descargar PDF
          </Button>
          <Button size="sm" className="gap-1.5 bg-green-600 hover:bg-green-700" disabled={busy} onClick={handleWhatsapp}>
            <MessageCircle className="h-4 w-4" /> Enviar por WhatsApp
          </Button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Form */}
        <div className="w-full max-w-md space-y-5 overflow-y-auto border-r border-slate-200 bg-white p-5">
          <section className="space-y-3">
            <h2 className="font-display text-sm font-bold uppercase tracking-wide text-slate-400">Encabezado</h2>
            <Field label="Título de marca"><Input value={draft.brandTitle} onChange={(e) => set({ brandTitle: e.target.value })} /></Field>
            <Field label="Subtítulo"><Textarea rows={2} value={draft.brandSubtitle} onChange={(e) => set({ brandSubtitle: e.target.value })} /></Field>
            <Field label="Título (Cotización)"><Input value={draft.heading} onChange={(e) => set({ heading: e.target.value })} /></Field>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-sm font-bold uppercase tracking-wide text-slate-400">Descripción</h2>
            <Field label="Título"><Input value={draft.descriptionTitle} onChange={(e) => set({ descriptionTitle: e.target.value })} /></Field>
            <Field label="Texto"><Textarea rows={3} value={draft.descriptionBody} onChange={(e) => set({ descriptionBody: e.target.value })} /></Field>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-sm font-bold uppercase tracking-wide text-slate-400">Servicios</h2>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Encabezado 1"><Input value={draft.serviceHeader} onChange={(e) => set({ serviceHeader: e.target.value })} /></Field>
              <Field label="Encabezado 2"><Input value={draft.totalHeader} onChange={(e) => set({ totalHeader: e.target.value })} /></Field>
            </div>
            <div className="space-y-3 rounded-xl bg-slate-50 p-3">
              {draft.items.map((it, i) => (
                <div key={it.id} className="space-y-2 rounded-lg border border-slate-200 bg-white p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-500">Fila {i + 1}</span>
                    <button onClick={() => removeItem(it.id)} className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <Input placeholder="Servicio" value={it.service} onChange={(e) => setItem(it.id, { service: e.target.value })} />
                  <Input placeholder="Precio / total" value={it.total} onChange={(e) => setItem(it.id, { total: e.target.value })} />
                </div>
              ))}
              <Button variant="outline" size="sm" className="w-full gap-1.5" onClick={addItem}><Plus className="h-4 w-4" /> Agregar fila</Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Etiqueta sub-total"><Input value={draft.subtotalLabel} onChange={(e) => set({ subtotalLabel: e.target.value })} /></Field>
              <Field label="Sub-total"><Input value={draft.subtotal} onChange={(e) => set({ subtotal: e.target.value })} /></Field>
              <Field label="Etiqueta total"><Input value={draft.totalLabel} onChange={(e) => set({ totalLabel: e.target.value })} /></Field>
              <Field label="Total"><Input value={draft.total} onChange={(e) => set({ total: e.target.value })} /></Field>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-sm font-bold uppercase tracking-wide text-slate-400">Cliente y pago</h2>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Etiqueta cliente"><Input value={draft.clientLabel} onChange={(e) => set({ clientLabel: e.target.value })} /></Field>
              <Field label="Nombre cliente"><Input value={draft.clientName} onChange={(e) => set({ clientName: e.target.value })} /></Field>
            </div>
            <Field label="Nota de entrega"><Input value={draft.deliveryNote} onChange={(e) => set({ deliveryNote: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Etiqueta pago"><Input value={draft.paymentLabel} onChange={(e) => set({ paymentLabel: e.target.value })} /></Field>
              <Field label="Método de pago"><Input value={draft.paymentMethod} onChange={(e) => set({ paymentMethod: e.target.value })} /></Field>
            </div>
            <Field label="Datos de cuenta"><Textarea rows={3} value={draft.accountInfo} onChange={(e) => set({ accountInfo: e.target.value })} /></Field>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-sm font-bold uppercase tracking-wide text-slate-400">Pie y WhatsApp</h2>
            <Field label="Nota 1"><Textarea rows={2} value={draft.footerNote} onChange={(e) => set({ footerNote: e.target.value })} /></Field>
            <Field label="Nota 2"><Input value={draft.footerNote2} onChange={(e) => set({ footerNote2: e.target.value })} /></Field>
            <Field label="WhatsApp (con lada, sin +)"><Input value={draft.whatsapp} onChange={(e) => set({ whatsapp: e.target.value })} placeholder="52155..." /></Field>
          </section>
        </div>

        {/* Preview */}
        <div className="hidden flex-1 items-start justify-center overflow-auto bg-slate-300 p-8 lg:flex">
          <div style={{ width: 794, transform: "scale(0.62)", transformOrigin: "top center" }} className="shadow-2xl">
            <QuotePreview ref={previewRef} quote={draft} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteBuilder;
